import type { LucideIcon } from 'lucide-react'
import { School, Map, GraduationCap, Building2 } from 'lucide-react'
import { env } from './env'
import { TENANT_ID } from './tenant'

export type LandingStat = {
  value: string
  label: string
  icon?: LucideIcon
  iconColor?: string
}

export type LandingAward = {
  imageUrl: string
  label: string
  href?: string
}

export type LandingProgram = {
  title: string
  description: string
  imageUrl: string
  href: string
}

export type TenantLandingPrograms = {
  heading: string
  subheading: string
  items: LandingProgram[]
}

// Full landing-page restyle modelled on the tenant's own website: co-branded hero
// with serif headline, gradient ticker strip, and tenant-specific search prompts.
export type TenantLandingTheme = {
  /** CSS `background` value for the hero section */
  heroBackground: string
  heroPartner: { logoUrl: string; name: string }
  heroHeading: string
  heroSubheading: string
  heroCta: { label: string; to: string }
  searchSuggestions: string[]
  ticker: string[]
  tickerBackground: string
}

export type TenantFooterConfig = {
  logoUrl: string
  logoAlt: string
  bg: string
  address: string
  orgName: string
  supportEmail: string
  aboutUsUrl: string
  privacyPolicyUrl: string
}

export type TenantLandingWordmark = {
  primary: string
  accent: string
}

export type TenantLandingConfig = {
  regionLogoUrl: string
  stats: LandingStat[]
  awards: LandingAward[]
  programs: TenantLandingPrograms | null
  theme: TenantLandingTheme | null
  statsBg: string
  statsDark: boolean
  watermarkUrl: string | null
  motto: string | null
  headerWordmark: string | null
  headerWordmarkAccent: string | null
  headerWordmarkClass: string | null
  headerWordmarkAccentClass: string | null
  headerSchoolName: string | null
  headerTagline: string | null
  headerNavClass: string | null
  headerNavActiveClass: string | null
  headerNavInactiveClass: string | null
  landingWordmark: TenantLandingWordmark | null
  catalogHeroGradient: boolean
  footerSproutMark: boolean
  statsSolidBar: boolean
  featureIconHoverClass: string | null
  footerArkansasLayout: boolean
  footerLight: boolean
  footerTagline: string | null
  externalCalendarUrl: string | null
  externalCatalogUrl: string | null
  externalResourcesUrl: string | null
  showHybridEvents: boolean
  onDemandOnly: boolean
  footer: TenantFooterConfig
}

// ---------------------------------------------------------------------------
// Stats — kept in code because they reference Lucide icon components which
// can't be serialised into a configmap. Add a new entry here when onboarding
// a new tenant. Everything else (colours, logos, text) is env-var driven.
// ---------------------------------------------------------------------------
const TENANT_STATS: Record<string, LandingStat[]> = {
  tx_esc_04: [
    { value: '8',       label: 'Counties'   },
    { value: '47',      label: 'Districts'  },
    { value: '43',      label: 'Charters'   },
    { value: '1.2M',    label: 'Students'   },
    { value: '108,136', label: 'Educators'  },
  ],
  tx_r20: [
    { value: '58',      label: 'Districts & 30 Charters', icon: School,        iconColor: 'var(--color-secondary)' },
    { value: '19',      label: 'Counties',                icon: Map,           iconColor: 'var(--color-secondary)' },
    { value: '487,587', label: 'Students',                icon: GraduationCap, iconColor: 'var(--color-secondary)' },
    { value: '760',     label: 'Total Campuses',          icon: Building2,     iconColor: 'var(--color-secondary)' },
  ],
  tx_r10: [
    { value: '130+',     label: 'Districts',  icon: School,        iconColor: 'var(--color-secondary)' },
    { value: '10',       label: 'Counties',   icon: Map,           iconColor: 'var(--color-secondary)' },
    { value: '900,000',  label: 'Students',   icon: GraduationCap, iconColor: 'var(--color-secondary)' },
    { value: '1,220+',   label: 'Campuses',   icon: Building2,     iconColor: 'var(--color-secondary)' },
  ],
  tx_r12: [
    { value: '77',      label: 'Districts',       icon: School,        iconColor: 'var(--color-secondary)' },
    { value: '12',      label: 'Counties',        icon: Map,           iconColor: 'var(--color-secondary)' },
    { value: '177,000', label: 'Students',        icon: GraduationCap, iconColor: 'var(--color-secondary)' },
    { value: '9',       label: 'Charter Schools', icon: Building2,     iconColor: 'var(--color-secondary)' },
  ],
  ar_esc: [
    { value: '75',      label: 'Counties'  },
    { value: '234',     label: 'Districts' },
    { value: '1,100+',  label: 'Schools'   },
    { value: '480K',    label: 'Students'  },
    { value: '38,000',  label: 'Educators' },
  ],
  tx_r9: [
    { value: '12',      label: 'Counties',         },
    { value: '37',      label: 'Districts',        },
    { value: '36,000+', label: 'Students',         },
    { value: '105',     label: 'Campuses',         },
    { value: '5.5k+',   label: 'Staff',            },
  ],
}

// Awards — tenants listed here get an awards badge strip on the landing page
// in place of the stats strip.
const TENANT_AWARDS: Record<string, LandingAward[]> = {
  jason: [
    { imageUrl: '/jason-awards/best-of-stem-winner-2026.png',   label: 'Best of STEM Winner 2026',   href: 'https://jason.org/best-of-stem-2026/' },
    { imageUrl: '/jason-awards/best-of-stem-finalist-2025.png', label: 'Best of STEM Finalist 2025', href: 'https://jason.org/2025-best-of-stem-awards/' },
    { imageUrl: '/jason-awards/best-of-stem-winner-2024.png',   label: 'Best of STEM Winner 2024',   href: 'https://jason.org/2024-best-of-stem-awards/' },
    { imageUrl: '/jason-awards/best-of-stem-finalist-2024.png', label: 'Best of STEM Finalist 2024', href: 'https://jason.org/2024-best-of-stem-awards/' },
    { imageUrl: '/jason-awards/best-of-stem-winner-2023.png',   label: 'Best of STEM Winner 2023',   href: 'https://jason.org/best-of-stem-beyondthewaterbottle/' },
    { imageUrl: '/jason-awards/best-of-stem-winner-2022.png',   label: 'Best of STEM Winner 2022',   href: 'https://jason.org/jason-learning-wins-two-best-of-stem-2022-awards/' },
  ],
}

// Programs — tenants listed here get program cards on the landing page in
// place of the generic platform feature cards.
const TENANT_PROGRAMS: Record<string, TenantLandingPrograms> = {
  jason: {
    heading: "Explore JASON's Award-Winning Curricula",
    subheading: 'Real-world STEM content created with real scientists, partners, and educators.',
    items: [
      {
        title: 'Playwatch Kids',
        description: 'Diverse, inclusive early-learning videos paired with hands-on JASON activities to use before, during, or after viewing.',
        imageUrl: '/jason-programs/playwatch-kids.jpg',
        href: 'https://jason.org/playwatch-kids/',
      },
      {
        title: 'EcoVillage',
        description: 'A student-led program where learners build a town in the outdoors, deciding what businesses, services, and resources their community needs to thrive.',
        imageUrl: '/jason-programs/ecovillage.png',
        href: 'https://jason.org/portfolio_item/ecovillage-greenacres-foundation/',
      },
      {
        title: 'ReMA Recycling Activities Collection',
        description: 'Free STEM lessons co-created with the Recycled Materials Association that bring the science and systems of recycling into the classroom.',
        imageUrl: '/jason-programs/rema-recycling.png',
        href: 'https://jason.org/rema/',
      },
      {
        title: 'Pathways to Your Future',
        description: 'Career-exploration modules that help students discover their passions, research career pathways, and build a personalized road map.',
        imageUrl: '/jason-programs/pathways-to-your-future.jpg',
        href: 'https://jason.org/portfolio_item/pathways-to-future/',
      },
      {
        title: 'Design & Pitch Challenges in STEM',
        description: 'Students meet inventors and entrepreneurs, tackle real-world challenges, and pitch innovative solutions to a panel of investors.',
        imageUrl: '/jason-programs/design-and-pitch.png',
        href: 'https://jason.org/portfolio_item/design-and-pitch/',
      },
      {
        title: 'VIMS – Marine Science: Microplastics',
        description: 'With the Virginia Institute of Marine Science, students investigate microplastic pollution and design solutions to protect aquatic environments.',
        imageUrl: '/jason-programs/vims-marine-science.jpg',
        href: 'https://jason.org/portfolio_item/vims-marine-science-microplastics/',
      },
    ],
  },
}

const TENANT_LANDING_THEMES: Record<string, TenantLandingTheme> = {
  jason: {
    heroBackground: [
      'radial-gradient(circle at 12% 18%, rgba(0, 153, 153, 0.45) 0%, transparent 42%)',
      'radial-gradient(circle at 88% 85%, rgba(242, 98, 34, 0.30) 0%, transparent 40%)',
      'linear-gradient(135deg, #052B3D 0%, #064A71 55%, #086296 100%)',
    ].join(', '),
    heroPartner: { logoUrl: '/region4-developworks-logo-white.png', name: 'DevelopWorks' },
    heroHeading: 'Award-Winning STEM Learning, Delivered on DevelopWorks',
    heroSubheading: "JASON Learning's real-world STEM curricula meet DevelopWorks' professional learning platform, with on-demand courses and certificates in one place.",
    heroCta: { label: 'Browse On-Demand Courses', to: '/ondemand' },
    searchSuggestions: [
      'Find on-demand STEM courses',
      'Marine science lessons for middle school',
      '¿Qué cursos de ciencias ofrecen?',
      'Early childhood STEM activities',
      'How do I get my certificate?',
    ],
    ticker: [
      'Award-winning STEM curricula',
      'Created with real scientists',
      'Aligned to state standards',
      'Learn on demand, anytime',
      'Best of STEM winner 2026',
    ],
    tickerBackground: 'linear-gradient(90deg, #3648D6 0%, #FF7530 100%)',
  },
}

const TENANT_MOTTO: Record<string, string> = {
  tx_r10: 'Students. Service. Solutions.',
}

const TENANT_CHROME: Record<string, {
  headerWordmark?: string
  headerSchoolName?: string
  headerTagline?: string
  headerWordmarkAccent?: string
  headerWordmarkClass?: string
  headerWordmarkAccentClass?: string
  headerNavClass?: string
  headerNavActiveClass?: string
  headerNavInactiveClass?: string
  landingWordmark?: TenantLandingWordmark
  catalogHeroGradient: boolean
  footerLogoAlt: string
  footerSproutMark: boolean
  statsSolidBar?: boolean
  featureIconHoverClass?: string
  footerArkansasLayout?: boolean
  footerLight?: boolean
  footerTagline?: string
}> = {
  ar_ade: {
    headerWordmark: 'Arkansas',
    landingWordmark: { primary: 'Arkansas', accent: 'ESC' },
    catalogHeroGradient: true,
    footerLogoAlt: 'Arkansas ADE DevelopWorks',
    footerSproutMark: true,
  },
  ar_esc: {
    headerWordmark: 'Develop',
    headerWordmarkAccent: 'Works',
    headerWordmarkClass: 'flex items-baseline text-lg font-semibold tracking-tight text-ar-navy font-manrope',
    headerWordmarkAccentClass: 'text-ar-red',
    headerNavClass: 'inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium font-manrope transition-colors whitespace-nowrap no-underline hover:no-underline',
    headerNavActiveClass: 'bg-ar-navy text-white shadow-sm',
    headerNavInactiveClass: 'text-ar-navy/80 hover:bg-white/70 hover:text-ar-navy',
    catalogHeroGradient: false,
    footerLogoAlt: 'Arkansas ESC DevelopWorks',
    footerSproutMark: true,
    statsSolidBar: true,
    featureIconHoverClass: 'group-hover:from-ar-red group-hover:to-ar-red',
    footerArkansasLayout: true,
    footerTagline: 'Your trusted platform for professional learning across the Arkansas Education Service Cooperatives.',
  },
  tx_r9: {
    headerSchoolName: 'Region 9 Education Service Center',
    headerTagline: 'Every learner. Every opportunity. Every day.',
    headerNavClass: 'inline-flex items-center gap-1 px-1.5 xl:px-2.5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap no-underline hover:no-underline',
    headerNavActiveClass: 'rounded-lg bg-primary-500 text-white shadow-inner',
    headerNavInactiveClass: 'rounded-lg text-primary-500 hover:bg-primary-50 hover:text-primary-500',
    catalogHeroGradient: false,
    footerLogoAlt: 'Region 9 Education Service Center',
    footerSproutMark: false,
    footerLight: true,
    footerTagline: 'Every learner. Every opportunity. Every day.',
  },
}

// Footer "About Us" link — tenants with their own about page point at it,
// everyone else falls back to the escDevelopWorks site.
const TENANT_ABOUT_US_URLS: Record<string, string> = {
  tx_r10: 'https://www.region10.org/o/r10esc/page/about',
  tx_r9: 'https://www.esc9.net/',
}

// Footer "Privacy" link — tenants publish their own privacy policy, everyone
// else falls back to the Region 4 policy.
const TENANT_PRIVACY_POLICY_URLS: Record<string, string> = {
  tx_r10: 'https://files-backend.assets.thrillshare.com/documents/asset/uploaded_file/3133/R10_Esc/a7ae9b66-86fc-4a9e-bbc3-41902f6390ad/R10_Internet-Privacy-Policy_acc.pdf?disposition=inline',
}

// External nav URLs — when set, the flag switches the nav link to open this URL
// in a new tab instead of the internal route.
const TENANT_NAV_URLS: Record<string, { calendarUrl?: string; catalogUrl?: string; resourcesUrl?: string }> = {
  tx_r10: {
    // Calendar and catalog intentionally omitted — tx_r10 uses the native
    // /calendar and /catalog routes.
  },
  tx_esc_04: {
    resourcesUrl: 'https://esc4.net/escdevelopworks/#resources',
  },
}

// Tenants that don't run hybrid events — the Sessions nav omits the link.
const TENANTS_WITHOUT_HYBRID_EVENTS = new Set(['tx_r8'])

// Tenants that only offer on-demand courses — the nav drops Calendar, Catalog,
// and the Sessions dropdown in favour of a single On Demand link.
const TENANTS_ON_DEMAND_ONLY = new Set(['jason'])

// ---------------------------------------------------------------------------
// Everything below reads from env vars injected at pod startup via
// docker-entrypoint.sh / configmap — zero code change needed for new tenants.
// ---------------------------------------------------------------------------

export const TENANT_LANDING: TenantLandingConfig = {
  regionLogoUrl: env('VITE_REGION_LOGO_URL')        || '/region4-logo.png',
  stats:         TENANT_STATS[TENANT_ID]            ?? TENANT_STATS['tx_esc_04'],
  awards:        TENANT_AWARDS[TENANT_ID]           ?? [],
  programs:      TENANT_PROGRAMS[TENANT_ID]         ?? null,
  theme:         TENANT_LANDING_THEMES[TENANT_ID]   ?? null,
  statsBg:       env('VITE_LANDING_STATS_BG')       || '#1a4a5c',
  statsDark:     env('VITE_LANDING_STATS_DARK')     !== 'false',
  watermarkUrl:  TENANT_CHROME[TENANT_ID]?.statsSolidBar
    ? null
    : (env('VITE_LANDING_WATERMARK_URL') || null),
  motto:               TENANT_MOTTO[TENANT_ID]                      ?? null,
  headerWordmark:      TENANT_CHROME[TENANT_ID]?.headerWordmark     ?? null,
  headerWordmarkAccent: TENANT_CHROME[TENANT_ID]?.headerWordmarkAccent ?? null,
  headerWordmarkClass: TENANT_CHROME[TENANT_ID]?.headerWordmarkClass ?? null,
  headerWordmarkAccentClass: TENANT_CHROME[TENANT_ID]?.headerWordmarkAccentClass ?? null,
  headerSchoolName:    TENANT_CHROME[TENANT_ID]?.headerSchoolName    ?? null,
  headerTagline:       TENANT_CHROME[TENANT_ID]?.headerTagline       ?? null,
  headerNavClass:      TENANT_CHROME[TENANT_ID]?.headerNavClass      ?? null,
  headerNavActiveClass: TENANT_CHROME[TENANT_ID]?.headerNavActiveClass ?? null,
  headerNavInactiveClass: TENANT_CHROME[TENANT_ID]?.headerNavInactiveClass ?? null,
  landingWordmark:     TENANT_CHROME[TENANT_ID]?.landingWordmark    ?? null,
  catalogHeroGradient: TENANT_CHROME[TENANT_ID]?.catalogHeroGradient ?? false,
  footerSproutMark:    TENANT_CHROME[TENANT_ID]?.footerSproutMark    ?? false,
  statsSolidBar:       TENANT_CHROME[TENANT_ID]?.statsSolidBar       ?? false,
  featureIconHoverClass: TENANT_CHROME[TENANT_ID]?.featureIconHoverClass ?? null,
  footerArkansasLayout: TENANT_CHROME[TENANT_ID]?.footerArkansasLayout ?? false,
  footerLight:         TENANT_CHROME[TENANT_ID]?.footerLight         ?? false,
  footerTagline:       TENANT_CHROME[TENANT_ID]?.footerTagline       ?? null,
  externalCalendarUrl: TENANT_NAV_URLS[TENANT_ID]?.calendarUrl       ?? null,
  externalCatalogUrl:  TENANT_NAV_URLS[TENANT_ID]?.catalogUrl        ?? null,
  externalResourcesUrl: TENANT_NAV_URLS[TENANT_ID]?.resourcesUrl     ?? null,
  showHybridEvents:    !TENANTS_WITHOUT_HYBRID_EVENTS.has(TENANT_ID),
  onDemandOnly:        TENANTS_ON_DEMAND_ONLY.has(TENANT_ID),
  footer: {
    // Chrome tenants skip the Region 4 footer logo when unset — otherwise ADE
    // would render Houston branding next to the sprout mark.
    logoUrl:      env('VITE_FOOTER_LOGO_URL')       || (TENANT_CHROME[TENANT_ID] ? '' : '/Region4 DevelopWorks Logo - 72PPI_White.png'),
    logoAlt:      env('VITE_FOOTER_LOGO_ALT')       || TENANT_CHROME[TENANT_ID]?.footerLogoAlt || 'DevelopWorks',
    bg:           env('VITE_FOOTER_BG')             || '#052B3D',
    address:      env('VITE_FOOTER_ADDRESS')        || 'Region 4 Education Service Center, 7145 West Tidwell Road, Houston, Texas 77092-2096',
    orgName:      env('VITE_FOOTER_ORG_NAME')       || 'Region 4 Education Service Center',
    supportEmail: env('VITE_FOOTER_SUPPORT_EMAIL')  || (TENANT_ID === 'tx_r9' ? '' : 'register@esc4.net'),
    aboutUsUrl:   TENANT_ABOUT_US_URLS[TENANT_ID]   ?? 'https://escdevelopworks.com/',
    privacyPolicyUrl: TENANT_PRIVACY_POLICY_URLS[TENANT_ID] ?? 'https://esc4.net/privacy-policy/',
  },
}
